/* ═══════════════════════════════════════════
   CART DRAWER — cart-drawer.js
   Dynamic, real-data version of the B1G1 cart drawer.
   Load this AFTER theme.js on every page (theme.liquid
   already includes the <script> tag — see setup notes).

   This file OWNS the cart: it replaces fetchCart,
   updateCartUI, addToCart, updateCartItem, openCart,
   closeCart and quickAddToCart from theme.js.
   → Delete those functions from theme.js (see the
     "CART" and "QUICK ADD" sections) so there are no
     duplicate/conflicting definitions.

   v2.3 CHANGES:
   - Restored .shiprocket-headless div + original
     button classes (name="checkout", .btn.btn-primary.btn-full)
     in the footer so the timezone-based toggle script in
     theme.liquid's <head> can find and show/hide them again.
   - isCaseItem() is now case-insensitive / trimmed so the
     B1G1 progress bar doesn't get stuck on a product_type
     mismatch like "case" vs "Case".
   ═══════════════════════════════════════════ */

'use strict';

/* ─────────────────────────────────────────────
   VIEWPORT HEIGHT FIX (mobile browsers)
   `100vh` on phones includes the address bar, which
   pushes the checkout button off-screen. This sets a
   real pixel value the CSS uses instead.
   ───────────────────────────────────────────── */
function cdSetViewportHeight() {
  document.documentElement.style.setProperty('--cd-vh', (window.innerHeight * 0.01) + 'px');
}
cdSetViewportHeight();
window.addEventListener('resize', cdSetViewportHeight);
window.addEventListener('orientationchange', cdSetViewportHeight);

/* ─────────────────────────────────────────────
   CONFIG — the only things you should need to edit
   ───────────────────────────────────────────── */
const CartDrawerConfig = {
  // Which word identifies a "case" product_type for the B1G1 progress
  // bar. This is DISPLAY ONLY — your live Automatic Discount in Shopify
  // still decides the real price. isCaseItem() below does a trimmed,
  // case-insensitive SUBSTRING match against this word, so "Case",
  // "Cases", "case", "Phone Case", etc. all match — no need to keep
  // this in exact sync with your product_type spelling/plural.
  caseProductType: 'case',

  // Rs. off shown for the "pay online, extra off" nudge. Pulled from
  // window.BOLDACC (set in theme.liquid) so it stays in sync with the
  // rest of the site; falls back to 100 if that isn't present.
  get prepaidOff() {
    return window.BOLDACC && window.BOLDACC.prepaidDiscount
      ? window.BOLDACC.prepaidDiscount / 100
      : 100;
  },
  get partialCodAdvance() {
    return window.BOLDACC && window.BOLDACC.partialCodAdvance
      ? window.BOLDACC.partialCodAdvance
      : 100;
  },
  get dispatchCutoffHourIST() {
    return window.BOLDACC && window.BOLDACC.dispatchCutoffHourIST
      ? window.BOLDACC.dispatchCutoffHourIST
      : 14;
  },
  get rating() { return (window.BOLDACC && window.BOLDACC.rating) || '4.9/5'; },
  get ratingCount() { return (window.BOLDACC && window.BOLDACC.ratingCount) || ''; },
  get review() { return (window.BOLDACC && window.BOLDACC.review) || null; }
};

/* ─────────────────────────────────────────────
   STATE
   ───────────────────────────────────────────── */
let cdCart = { items: [], item_count: 0, total_price: 0, total_discount: 0 };

const cdInr = n => '₹' + Math.round(n).toLocaleString('en-IN');

// FIX: case-insensitive SUBSTRING match, so "Case", "Cases", " Case ",
// "Phone Case", etc. all count toward B1G1 — no exact-spelling dependency.
const isCaseItem = item =>
  (item.product_type || '').trim().toLowerCase().includes(CartDrawerConfig.caseProductType.trim().toLowerCase());

/* ─────────────────────────────────────────────
   FETCH / RENDER
   ───────────────────────────────────────────── */
async function fetchCart() {
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return;
    cdCart = await res.json();
    updateCartUI();
  } catch (e) { /* silent — network hiccup, next action will retry */ }
}

function updateCartUI() {
  const countEl = document.getElementById('cartCount');
  if (countEl) {
    countEl.textContent = cdCart.item_count || 0;
    countEl.style.display = (cdCart.item_count || 0) > 0 ? 'flex' : 'none';
  }
  const mount = document.getElementById('cartDrawerMount');
  if (!mount) return;
  const scrollTop = mount.querySelector('.cd-body') ? mount.querySelector('.cd-body').scrollTop : 0;
  mount.innerHTML = cdView();
  const body = mount.querySelector('.cd-body');
  if (body) body.scrollTop = scrollTop;
  cdPaintTicker(false);
}

/* ─────────────────────────────────────────────
   B1G1 PROGRESS (display only — real price comes
   straight from cart.js via the live discount)
   ───────────────────────────────────────────── */
function cdCompute() {
  let caseCount = 0;
  cdCart.items.forEach(item => { if (isCaseItem(item)) caseCount += item.quantity; });

  const freeCount = Math.floor(caseCount / 2);
  const pending = caseCount % 2 === 1;
  const saved = (cdCart.total_discount || 0) / 100;
  const mrp = (cdCart.items || []).reduce((s, i) => s + (i.original_line_price || i.line_price) / 100, 0);
  const total = (cdCart.total_price || 0) / 100;

  return {
    caseCount, freeCount, pending, saved, mrp, total,
    count: cdCart.item_count || 0
  };
}

/* ─────────────────────────────────────────────
   PIECES
   ───────────────────────────────────────────── */
const CD_ICON = {
  shield: '<svg viewBox="0 0 24 24"><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z"/><path d="M9 12l2 2 4-4"/></svg>',
  swap:   '<svg viewBox="0 0 24 24"><path d="M4 8h13l-3-3M20 16H7l3 3"/></svg>',
  truck:  '<svg viewBox="0 0 24 24"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z"/><circle cx="7.5" cy="17.5" r="1.5"/><circle cx="17.5" cy="17.5" r="1.5"/></svg>',
  lock:   '<svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 018 0v3"/></svg>',
  bag:    '<svg viewBox="0 0 24 24"><path d="M5 8h14v11a2 2 0 01-2 2H7a2 2 0 01-2-2zM5 8l2-4h10l2 4"/><path d="M9 12a3 3 0 006 0"/></svg>'
};

function cdOfferBar(c) {
  let t, s, w, cls = '';
  if (c.caseCount === 0) {
    t = 'Buy 1 Get 1 <em>FREE</em>';
    s = 'Add any 2 cases. Pay for one. Discount applies by itself.';
    w = 4;
  } else if (c.pending && c.freeCount === 0) {
    t = 'Your <em>FREE</em> case is waiting';
    s = 'Add one more case below. You pay nothing extra.';
    w = 50;
  } else if (c.pending) {
    t = `${c.freeCount} free case${c.freeCount > 1 ? 's' : ''} unlocked. Add 1 more for <em>another FREE</em>`;
    s = 'Every second case is on us.';
    w = 50;
  } else {
    t = `✓ ${c.freeCount} FREE case${c.freeCount > 1 ? 's' : ''} unlocked`;
    s = c.saved > 0 ? `You are saving ${cdInr(c.saved)} on this order.` : 'Add 2 cases to unlock your free one.';
    w = c.freeCount > 0 ? 100 : 4;
    cls = c.freeCount > 0 ? ' cd-unlocked' : '';
  }
  return `<div class="cd-offer${cls}"><div class="cd-t">${t}</div><div class="cd-s">${s}</div>
    <div class="cd-track"><i style="width:${w}%"></i></div>
    <div class="cd-steps"><b class="${c.caseCount >= 1 ? 'cd-done' : ''}">1st case</b><b class="${w === 100 ? 'cd-done' : ''}">2nd case FREE</b></div></div>`;
}

function cdLine(item) {
  const hasDiscount = (item.total_discount || 0) > 0;
  const originalTotal = (item.original_line_price || item.line_price) / 100;
  const finalTotal = item.final_line_price / 100;
  const tag = hasDiscount
    ? `<span class="cd-tag">${finalTotal === 0 ? 'Free with B1G1' : cdInr(item.total_discount / 100) + ' off'}</span>`
    : '';
  const price = hasDiscount
    ? `<s>${cdInr(originalTotal)}</s><span class="${finalTotal === 0 ? 'cd-free' : ''}">${finalTotal === 0 ? 'FREE' : cdInr(finalTotal)}</span>`
    : cdInr(finalTotal);
  return `<div class="cd-item" data-variant-id="${item.variant_id}">
    <a href="${item.url}" class="cd-thumb"><img src="${item.image || ''}" alt="${item.title}" loading="lazy" width="72" height="88"></a>
    <div class="cd-main">
      <div class="cd-title"><a href="${item.url}">${item.product_title}</a></div>
      <div class="cd-var">${item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
      ${tag}
      <div class="cd-row">
        <div class="cd-qty">
          <button data-act="dec" data-id="${item.variant_id}" aria-label="Decrease">−</button>
          <span>${item.quantity}</span>
          <button data-act="inc" data-id="${item.variant_id}" aria-label="Increase">+</button>
        </div>
        <button class="cd-rm" data-act="rm" data-id="${item.variant_id}">Remove</button>
      </div>
    </div>
    <div class="cd-price">${price}</div>
  </div>`;
}

function cdRail(c) {
  const picks = (window.CartUpsellProducts || [])
    .filter(p => !cdCart.items.some(i => i.variant_id === p.id))
    .slice(0, 4);
  if (!picks.length) return '';
  const h = c.caseCount === 0 ? 'Bestsellers' : c.pending ? 'Pick your FREE case' : 'Add 2 more, get 1 more free';
  const sub = c.pending ? 'One tap. Added at ₹0.' : 'Gift one, keep one';
  return `<div class="cd-rail-wrap"><div class="cd-rail-h"><h3>${h}</h3><span>${sub}</span></div><div class="cd-rail">` +
    picks.map(p => `<div class="cd-card">
      <div class="cd-thumb"><img src="${p.image || ''}" alt="${p.title}" loading="lazy"></div>
      <h4>${p.title}</h4>
      <div class="cd-p">${p.compareAtPrice > p.price ? `<s>${cdInr(p.compareAtPrice / 100)}</s>` : ''}${cdInr(p.price / 100)}</div>
      <button data-act="add" data-id="${p.id}">Add</button>
    </div>`).join('') +
    `</div></div>`;
}

function cdAddons() {
  const addons = window.CartAddons || [];
  if (!addons.length) return '';
  return `<div class="cd-addons">` + addons.map(a => {
    const on = cdCart.items.some(i => i.variant_id === a.variantId);
    return `<div class="cd-addon${on ? ' cd-on' : ''}" data-act="toggle" data-id="${a.variantId}" role="button" tabindex="0">
      <div class="cd-ck">${on ? '✓' : ''}</div>
      <div class="cd-n">${a.title}<small>${a.note || ''}</small></div>
      <div class="cd-pr">+ ${cdInr(a.price / 100)}</div>
    </div>`;
  }).join('') + `</div>`;
}

const cdTrust = () => `<div class="cd-trust">
  <div class="cd-tb">${CD_ICON.shield}<div><b>1-year warranty</b><span>On every case. No questions.</span></div></div>
  <div class="cd-tb">${CD_ICON.swap}<div><b>Easy exchange</b><span>Wrong fit? We swap it.</span></div></div>
  <div class="cd-tb">${CD_ICON.truck}<div><b>Free shipping</b><span>Pan India, every order.</span></div></div>
  <div class="cd-tb">${CD_ICON.lock}<div><b>Secure payment</b><span>UPI, cards, partial COD.</span></div></div></div>
  ${CartDrawerConfig.ratingCount ? `<div class="cd-rating"><span class="cd-st">★★★★★</span><span><b>${CartDrawerConfig.rating}</b> from ${CartDrawerConfig.ratingCount} happy customers</span></div>` : ''}`;

const cdReview = () => {
  const r = CartDrawerConfig.review;
  return (r && r.text) ? `<div class="cd-review"><div class="cd-st">★★★★★</div><p>"${r.text}"</p><small>${r.by || ''}</small></div>` : '';
};

function cdTicker() {
  return cdLiveOrders().length ? `<div class="cd-ticker"><span class="cd-dot"></span><span class="cd-tx" data-cd-ticker></span></div>` : '';
}

function cdDispatchLine() {
  const ist = new Date(Date.now() + (330 + new Date().getTimezoneOffset()) * 60000);
  const mins = CartDrawerConfig.dispatchCutoffHourIST * 60 - (ist.getHours() * 60 + ist.getMinutes());
  if (mins <= 0) return 'Order now, ships <b>tomorrow morning</b>';
  return `Order in <b>${Math.floor(mins / 60)}h ${mins % 60}m</b> and it ships today`;
}

/* FIX: restored .shiprocket-headless div + original checkout button
   classes/name so the timezone-based toggle script in theme.liquid's
   <head> (checkAndSetButtonVisibility → renderButton) can find and
   show/hide these elements again for India vs non-India users. */
function cdFoot(c) {
  return `<div class="cd-foot">
    ${c.saved > 0 ? `<div class="cd-save">You are saving ${cdInr(c.saved)} with today's offer</div>` : ''}
    <div class="cd-tot"><span class="cd-l">Total (${c.count} item${c.count > 1 ? 's' : ''})</span><span class="cd-r">${c.saved > 0 ? `<s>${cdInr(c.mrp)}</s>` : ''}${cdInr(c.total)}</span></div>
    <div class="cd-ship"><span>Shipping</span><b>FREE</b></div>
    <div class="shiprocket-headless"></div>
    <button name="checkout" class="cd-cta btn btn-primary btn-full" data-act="checkout">Checkout<span class="cd-sep">|</span>${cdInr(c.total)}</button>
    <div class="cd-dispatch" data-cd-dispatch>${cdDispatchLine()}</div>
    <div class="cd-under"><span>Pay online, get extra ${cdInr(CartDrawerConfig.prepaidOff)} off</span><span>·</span><span>Partial COD at ${cdInr(CartDrawerConfig.partialCodAdvance)} advance</span></div>
    <div class="cd-pay"><i>UPI</i><i>VISA</i><i>MASTERCARD</i><i>RUPAY</i><i>COD</i></div>
  </div>`;
}

function cdView() {
  const c = cdCompute();
  const head = `<div class="cd-head"><h2>Your Cart${c.count ? `<small>${c.count} item${c.count > 1 ? 's' : ''}</small>` : ''}</h2><button class="cd-x" data-act="close" aria-label="Close">×</button></div>`;

  if (!cdCart.items.length) {
    return head + `<div class="cd-body">${cdOfferBar(c)}${cdTicker()}
      <div class="cd-empty">${CD_ICON.bag}<p>Your cart is empty</p><div class="cd-big">Pick any 2 cases. Pay for 1.</div>
      <a href="https://boldacc.co/" class="cd-cta" style="text-decoration:none" data-act="close">Start shopping</a></div>
      ${cdRail(c)}${cdTrust()}</div>`;
  }

  return head + `<div class="cd-body">${cdOfferBar(c)}${cdTicker()}
    <div class="cd-items">${cdCart.items.map(cdLine).join('')}</div>
    ${cdRail(c)}
    ${cdAddons() ? `<div class="cd-rail-h" style="padding-top:14px;padding-bottom:0"><h3>Protect it fully</h3><span>Most buyers add these</span></div>${cdAddons()}` : ''}
    <div class="cd-prepaid"><span class="cd-ic">⚡</span><span>Pay online and get <b>extra ${cdInr(CartDrawerConfig.prepaidOff)} off</b> plus priority dispatch.</span></div>
    ${cdTrust()}${cdReview()}</div>${cdFoot(c)}`;
}

/* ─────────────────────────────────────────────
   LIVE TICKER — wired but hidden until you feed
   it real orders. Populate window.CartRecentOrders
   from a review/social-proof app, or via Shopify
   Flow → metafield → Liquid, as
   [{ name, city, product, at (ms epoch) }, ...].
   Anything older than 24h is dropped automatically.
   ───────────────────────────────────────────── */
let cdTickerIdx = 0;
function cdLiveOrders() {
  const day = 864e5;
  return (window.CartRecentOrders || []).filter(o => Date.now() - o.at < day);
}
function cdAgo(ts) {
  const m = Math.max(1, Math.round((Date.now() - ts) / 60000));
  return m < 60 ? m + ' min ago' : Math.round(m / 60) + ' hr ago';
}
function cdPaintTicker(animate) {
  const list = cdLiveOrders();
  if (!list.length) return;
  const o = list[cdTickerIdx % list.length];
  const html = `<b>${o.name}</b> from ${o.city} ordered ${o.product} · ${cdAgo(o.at)}`;
  document.querySelectorAll('[data-cd-ticker]').forEach(el => {
    if (!animate) { el.innerHTML = html; return; }
    el.classList.add('cd-out');
    setTimeout(() => { el.innerHTML = html; el.classList.remove('cd-out'); }, 350);
  });
}
setInterval(() => { cdTickerIdx++; cdPaintTicker(true); }, 6000);
setInterval(() => document.querySelectorAll('[data-cd-dispatch]').forEach(e => e.innerHTML = cdDispatchLine()), 30000);

/* ─────────────────────────────────────────────
   CART ACTIONS (real Shopify AJAX API)
   ───────────────────────────────────────────── */
async function addToCart(variantId, quantity, properties) {
  try {
    const body = { items: [{ id: variantId, quantity: quantity || 1, properties: properties || {} }] };
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      cdToast(err.description || 'Could not add to cart. Please try again.', true);
      return;
    }
    await fetchCart();
    openCart();
    cdToast('Added to cart!');
  } catch (e) {
    cdToast('Error adding to cart. Please try again.', true);
  }
}

async function updateCartItem(variantId, quantity) {
  try {
    const updates = {};
    updates[variantId] = quantity;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    await fetchCart();
    if (document.querySelector('.cart-page')) window.location.reload();
  } catch (e) { /* silent */ }
}

function quickAddToCart(event, variantId) {
  if (event) { event.preventDefault(); event.stopPropagation(); }
  addToCart(variantId, 1);
}

function openCart() {
  cdSetViewportHeight();
  const drawer = document.getElementById('cartDrawer');
  const overlay = document.getElementById('cartOverlay');
  if (drawer) drawer.classList.add('is-open');
  if (overlay) { overlay.style.display = 'block'; setTimeout(() => overlay.classList.add('is-open'), 20); }
  document.body.style.overflow = 'hidden';
  fetchCart();
}

function closeCart() {
  const drawer = document.getElementById('cartDrawer');
  const overlay = document.getElementById('cartOverlay');
  if (drawer) drawer.classList.remove('is-open');
  if (overlay) { overlay.classList.remove('is-open'); setTimeout(() => { overlay.style.display = 'none'; }, 320); }
  document.body.style.overflow = '';
}

/* Lightweight toast — self-contained so load order vs theme.js doesn't matter */
let cdToastTimeout;
function cdToast(message, isError) {
  const toast = document.getElementById('toast');
  if (!toast) return;
  toast.textContent = message;
  toast.style.background = isError ? '#C0392B' : '#1A1714';
  toast.classList.add('is-visible');
  clearTimeout(cdToastTimeout);
  cdToastTimeout = setTimeout(() => toast.classList.remove('is-visible'), 3000);
}

/* ─────────────────────────────────────────────
   EVENTS (delegated — the drawer's inner HTML is
   fully re-rendered on every update, so listeners
   live on the static #cartDrawerMount wrapper)
   ───────────────────────────────────────────── */
document.addEventListener('click', function (e) {
  const mount = document.getElementById('cartDrawerMount');
  if (!mount || !mount.contains(e.target)) return;

  const closeBtn = e.target.closest('[data-act="close"]');
  if (closeBtn) { closeCart(); return; }

  const checkoutBtn = e.target.closest('[data-act="checkout"]');
  if (checkoutBtn) { window.location.href = '/checkout'; return; }

  const actionEl = e.target.closest('[data-act]');
  if (!actionEl) return;
  const id = actionEl.dataset.id;
  const line = cdCart.items.find(i => String(i.variant_id) === String(id));

  switch (actionEl.dataset.act) {
    case 'inc':
      if (line) updateCartItem(id, line.quantity + 1);
      break;
    case 'dec':
      if (line) updateCartItem(id, Math.max(0, line.quantity - 1));
      break;
    case 'rm':
      updateCartItem(id, 0);
      break;
    case 'add':
      addToCart(id, 1);
      break;
    case 'toggle':
      if (line) updateCartItem(id, 0); else addToCart(id, 1);
      break;
  }
});

document.addEventListener('DOMContentLoaded', fetchCart);